import { createSelector, createSlice } from "@reduxjs/toolkit";
import { selectGameObject } from "features/gamestate/gameStateSlice";
import Provider from "provider";
import { GAMESTATE_ROUND_FINAL } from "../gamestate/constants";
import logging from "utils/logging";
const log = logging.getLogger("Editor Slice");

const editorSlice = createSlice({
  name: "editor",
  initialState: {
    round: "round1",
    nameEditState: null,
    editingQuestion: null,
    editingCategory: null,
    savingCategory: false,
    savingQuestion: false,
    savingDetails: false,
    // — persistent edit panel state —
    panelMode: null, // null | 'clue' | 'category'
    selectedQuestionId: null, // for panelMode='clue'
    selectedCategoryIdx: null, // for panelMode='category'
    panelSaveStatus: "idle", // 'idle' | 'saving' | 'saved' | 'error'
    panelLastSavedAt: null, // ISO timestamp string
    panelSaveErrorMsg: null, // optional specific message for the error state
  },
  reducers: {
    editName(state, action) {
      state.nameEditState = "saving";
    },
    nameEdited(state, action) {
      state.nameEditState = "saved";
    },
    editQuestion(state, action) {
      state.editingQuestion = action.payload.questionId;
    },
    editCategory(state, action) {
      state.editingCategory = action.payload.categoryNum;
    },
    stopEditingCategory(state) {
      state.editingCategory = null;
    },
    stopEditingQuestion(state) {
      state.editingQuestion = null;
    },
    changeRound(state, action) {
      state.round = action.payload.round;
    },
    saveNewCategory(state) {
      state.savingCategory = true;
    },
    finishSavingNewCategory(state) {
      state.savingCategory = false;
      state.editingCategory = null;
    },
    saveQuestion(state) {
      state.savingQuestion = true;
    },
    finishSavingQuestion(state) {
      state.savingQuestion = false;
      state.editingQuestion = null;
    },
    savingDetails(state) {
      state.savingDetails = true;
    },
    finishSavingDetails(state) {
      state.savingDetails = false;
    },

    // — persistent edit panel reducers —
    selectClue(state, action) {
      const { questionId } = action.payload;
      if (state.panelMode === "clue" && state.selectedQuestionId === questionId) {
        // Toggle off — clicking the same cell deselects
        state.panelMode = null;
        state.selectedQuestionId = null;
      } else {
        state.panelMode = "clue";
        state.selectedQuestionId = questionId;
        state.selectedCategoryIdx = null;
      }
    },
    selectCategory(state, action) {
      const { categoryIdx } = action.payload;
      if (
        state.panelMode === "category" &&
        state.selectedCategoryIdx === categoryIdx
      ) {
        state.panelMode = null;
        state.selectedCategoryIdx = null;
      } else {
        state.panelMode = "category";
        state.selectedCategoryIdx = categoryIdx;
        state.selectedQuestionId = null;
      }
    },
    clearPanelSelection(state) {
      state.panelMode = null;
      state.selectedQuestionId = null;
      state.selectedCategoryIdx = null;
    },
    panelSaving(state) {
      state.panelSaveStatus = "saving";
      state.panelSaveErrorMsg = null;
    },
    panelSaved(state) {
      state.panelSaveStatus = "saved";
      state.panelLastSavedAt = new Date().toISOString();
      state.panelSaveErrorMsg = null;
    },
    panelSaveError(state, action) {
      state.panelSaveStatus = "error";
      state.panelSaveErrorMsg = action.payload?.message || null;
    },
    panelSaveReset(state) {
      state.panelSaveStatus = "idle";
      state.panelSaveErrorMsg = null;
    },
  },
});

export const {
  editQuestion,
  editCategory,
  stopEditingQuestion,
  stopEditingCategory,
  changeRound,
  // — persistent edit panel —
  selectClue,
  selectCategory,
  clearPanelSelection,
  panelSaving,
  panelSaved,
  panelSaveError,
  panelSaveReset,
} = editorSlice.actions;

const updateGameName = (gameId, newName) => async (dispatch) => {
  dispatch(editorSlice.actions.editName());

  await Provider.updateGameName(gameId, newName);

  dispatch(editorSlice.actions.nameEdited());
};

const updateCategoryAction =
  (gameId, roundId, categoryIndex, newCategory) => async (dispatch) => {
    dispatch(editorSlice.actions.saveNewCategory());

    if (roundId === GAMESTATE_ROUND_FINAL) {
      await Provider.updateFinalCategory(gameId, newCategory);
    } else {
      await Provider.updateCategory(
        gameId,
        roundId,
        categoryIndex,
        newCategory
      );
    }
    dispatch(editorSlice.actions.finishSavingNewCategory());
  };

const updateQuestionAction =
  (gameId, questionId, newQuestionData) => async (dispatch) => {
    dispatch(editorSlice.actions.saveQuestion());

    await Provider.updateQuestion(gameId, questionId, newQuestionData);

    dispatch(editorSlice.actions.finishSavingQuestion());
  };

const updateGameDetailsAction = (gameId, newDetails) => async (dispatch) => {
  dispatch(editorSlice.actions.savingDetails());

  await Provider.updateGameDetails(gameId, newDetails);

  dispatch(editorSlice.actions.finishSavingDetails());
};

export {
  updateGameName,
  updateCategoryAction,
  updateQuestionAction,
  updateGameDetailsAction,
};

/* --------- Selectors ----------- */
const selectEditorState = (state) => state.editor;
const selectEditingQuestionId = createSelector(
  selectEditorState,
  (e) => e.editingQuestion
);
const selectEditingNameState = createSelector(
  selectEditorState,
  (e) => e.nameEditState
);
// const selectEditingRound = createSelector(selectEditorState, (e) => e.round);
const selectEditingCategory = createSelector(
  selectEditorState,
  (e) => e.editingCategory
);
const selectSavingCategory = createSelector(
  selectEditorState,
  (e) => e.savingCategory
);
const selectSavingQuestion = createSelector(
  selectEditorState,
  (e) => e.savingQuestion
);
const selectSavingDetails = createSelector(
  selectEditorState,
  (e) => e.savingDetails
);

const selectPanelMode = createSelector(selectEditorState, (e) => e.panelMode);
const selectSelectedQuestionId = createSelector(
  selectEditorState,
  (e) => e.selectedQuestionId
);
const selectSelectedCategoryIdx = createSelector(
  selectEditorState,
  (e) => e.selectedCategoryIdx
);
const selectPanelSaveStatus = createSelector(
  selectEditorState,
  (e) => e.panelSaveStatus
);
const selectPanelLastSavedAt = createSelector(
  selectEditorState,
  (e) => e.panelLastSavedAt
);
const selectPanelSaveErrorMsg = createSelector(
  selectEditorState,
  (e) => e.panelSaveErrorMsg
);

const selectCategoryString = (round) =>
  createSelector(
    [selectEditingCategory, selectGameObject],
    (categoryIdx, gameObj) => {
      log.debug("Getting selected category: ", round, gameObj);
      if (round === GAMESTATE_ROUND_FINAL) {
        return gameObj["categories"]["final"].category;
      }
      const roundData = gameObj["categories"][round];
      return roundData[categoryIdx].category;
    }
  );

export {
  selectCategoryString,
  selectEditingQuestionId,
  selectEditingCategory,
  // selectEditingRound,
  selectEditingNameState,
  selectSavingCategory,
  selectSavingQuestion,
  selectSavingDetails,
  // — persistent edit panel —
  selectPanelMode,
  selectSelectedQuestionId,
  selectSelectedCategoryIdx,
  selectPanelSaveStatus,
  selectPanelLastSavedAt,
  selectPanelSaveErrorMsg,
};

export default editorSlice.reducer;
